import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ScoreProgressChart = ({ feedbackData, skillTag, onPointClick }) => {
  // Filter and sort feedback data for this specific skill tag
  const skillFeedback = feedbackData
    .filter(item => item.tag === skillTag && typeof item.score === 'number')
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // If no scored feedback, don't render the chart
  if (skillFeedback.length === 0) {
    return null;
  }

  // Prepare data for the chart
  const labels = skillFeedback.map(item => {
    const date = new Date(item.timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  });

  const scores = skillFeedback.map(item => item.score);

  const chartData = {
    labels,
    datasets: [
      {
        label: `${skillTag} Score`,
        data: scores,
        borderColor: '#ffbd39', // Primary color from CSS variables
        backgroundColor: 'rgba(255, 189, 57, 0.1)', // Semi-transparent primary color
        borderWidth: 3,
        pointBackgroundColor: '#ffbd39',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 10,
        pointHoverBackgroundColor: '#e6a800',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3,
        fill: true,
        tension: 0.4, // Smooth curves
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend since we only have one dataset
      },
      title: {
        display: true,
        text: `${skillTag} Progress Over Time`,
        color: '#ffbd39',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
      tooltip: {
        backgroundColor: '#111111',
        titleColor: '#ffbd39',
        bodyColor: '#dddddd',
        borderColor: '#ffbd39',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function(context) {
            const index = context[0].dataIndex;
            const item = skillFeedback[index];
            const date = new Date(item.timestamp);
            return date.toLocaleDateString('en-US', { 
              weekday: 'short',
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            });
          },
          label: function(context) {
            return `Score: ${context.parsed.y}`;
          },
          afterLabel: function(context) {
            const index = context.dataIndex;
            const item = skillFeedback[index];
            let label = item.comment ? `Comment: "${item.comment}"` : '';
            if (label) label += '\n';
            label += 'Click to view post';
            return label;
          }
        }
      },
    },
    elements: {
      point: {
        radius: 6,
        hoverRadius: 10,
        borderWidth: 2,
        hoverBorderWidth: 3,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Date',
          color: '#dddddd',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        ticks: {
          color: '#999999',
          maxTicksLimit: 8, // Limit number of x-axis labels
        },
        grid: {
          color: '#333333',
          drawBorder: false,
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Score',
          color: '#dddddd',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        min: 0,
        max: 5,
        offset: true,
        ticks: {
          color: '#999999',
          stepSize: 1,
          callback: function(value) {
            return `${value}`;
          },
        },
        grid: {
          color: '#333333',
          drawBorder: false,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && onPointClick) {
        const elementIndex = elements[0].index;
        const clickedFeedback = skillFeedback[elementIndex];
        if (clickedFeedback && clickedFeedback.postId) {
          onPointClick(clickedFeedback.postId);
        }
      }
    },
  };

  return (
    <div className="chart-container">
      <Line data={chartData} options={options} />
      <div className="chart-legend">
      </div>
    </div>
  );
};

export default ScoreProgressChart;
